import React, { useState } from 'react';
import { Button, Card, CardBody, CardHeader, Col, Row, Spinner } from 'reactstrap';
import Alert from '@/shared/components/Alert';
import { addCortPersonThroughSalesforce, lookUpCortPerson, moveCortPersonForward, runCortNightly } from '../qaToolsApi';

const DEFAULT_DAYS = 4;
const UNAVAILABLE = 'QA Tools are not available on this environment.';

const lastEmail = enrolment => (enrolment.last_email_day
  ? `Day ${enrolment.last_email_day} (${enrolment.last_sent_at} UTC)`
  : 'None yet');

const nextEmail = enrolment => {
  if (enrolment.stopped_at) {
    return `None: stopped ${enrolment.stopped_at} UTC (${enrolment.stop_reason})`;
  }
  if (!enrolment.next_email_day) {
    return 'None: the last email has been sent';
  }
  return `Day ${enrolment.next_email_day}, due ${enrolment.next_email_due_on}`;
};

const Field = ({ id, label, ...input }) => (
  <>
    <label className='form__form-group-label' htmlFor={id}>{label}</label>
    <input id={id} className='form-control' {...input} />
  </>
);

const PersonCard = ({ person }) => {
  if (!person.enrolment) {
    return (
      <>
        <p className='mb-1'><b>{person.name}</b>{` · ${person.email} · ${person.status}`}</p>
        <p>Not in CORT&apos;s activation emails.</p>
      </>
    );
  }

  const { enrolment } = person;
  return (
    <>
      <p className='mb-2'><b>{person.name}</b>{` · ${person.email} · ${person.status} · ${person.cort_program}`}</p>
      <table className='table table--bordered mb-0'>
        <tbody>
          <tr><th>Started</th><td>{enrolment.started_on}</td></tr>
          <tr><th>Today is</th><td data-testid='day-of-sequence'>{`Day ${enrolment.day_of_sequence}`}</td></tr>
          <tr><th>Last email sent</th><td data-testid='last-email'>{lastEmail(enrolment)}</td></tr>
          <tr><th>Next email</th><td data-testid='next-email'>{nextEmail(enrolment)}</td></tr>
        </tbody>
      </table>
    </>
  );
};

/**
 * Lets QA test CORT's activation emails without waiting days or involving CORT's
 * Salesforce: look a person up, move them forward, run the nightly job now, or add a
 * person the way Salesforce does. The API answers 404 outside QA and local.
 */
const CortActivationTool = () => {
  const [email, setEmail] = useState('');
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [newAgent, setNewAgent] = useState({ email: '', first_name: '', last_name: '' });
  const [person, setPerson] = useState(undefined);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [isBusy, setBusy] = useState(false);
  const [addedAgent, setAddedAgent] = useState(undefined);
  const [addError, setAddError] = useState(null);

  const act = async (work, success = null) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    const result = await work();
    setBusy(false);
    if (result.errors) {
      setError(result.errors);
      return result;
    }
    setMessage(success);
    return result;
  };

  const showPerson = result => {
    if (result.available === false) {
      setError(UNAVAILABLE);
      return;
    }
    if (!result.errors) {
      setPerson(result.person);
    }
  };

  const onLookUp = async () => showPerson(await act(() => lookUpCortPerson(email)));

  const onMoveForward = async () => showPerson(await act(() => moveCortPersonForward(person.id, Number(days))));

  const onRunNightly = async () => {
    const run = await act(runCortNightly, "Tonight's job ran.");
    if (run.errors || !person) {
      return;
    }
    const refreshed = await lookUpCortPerson(person.email);
    showPerson(refreshed);
  };

  const onAddThroughSalesforce = async () => {
    setBusy(true);
    setAddError(null);
    setAddedAgent(undefined);
    const result = await addCortPersonThroughSalesforce(newAgent);
    setBusy(false);
    if (result.errors) {
      setAddError(result.errors);
      return;
    }
    setAddedAgent(result.person);
  };

  const updateNewAgent = field => event => setNewAgent({ ...newAgent, [field]: event.target.value });

  return (
    <>
      <Row>
        <Col md={12}>
          <Card>
            <CardHeader><strong>CORT activation emails</strong></CardHeader>
            <CardBody>
              <div className='card__title'>
                <h5 className='subhead'>
                  Look a person up to see where they are in CORT&apos;s emails, move them forward in time, or run
                  tonight&apos;s job now. Emails are really sent; on QA they land in the QA mail catcher.
                </h5>
              </div>

              <Row>
                <Col lg={5} className='mb-3'>
                  <Field id='cort-lookup-email' label='Email' type='email' value={email} onChange={event => setEmail(event.target.value)} />
                </Col>
                <Col lg={2} className='mb-3 d-flex align-items-end'>
                  <Button color='primary' size='sm' className='mb-0' disabled={!email || isBusy} onClick={onLookUp}>Look up</Button>
                </Col>
              </Row>

              {person === null && <p>No one has that email.</p>}
              {person && <PersonCard person={person} />}

              <Row className='mt-3'>
                <Col lg={2} className='mb-3'>
                  <Field id='cort-move-days' label='Days' type='number' min={1} max={400} value={days} onChange={event => setDays(event.target.value)} />
                </Col>
                <Col lg={3} className='mb-3 d-flex align-items-end'>
                  <Button color='primary' size='sm' className='mb-0' disabled={!person?.enrolment || isBusy} onClick={onMoveForward}>Move forward</Button>
                </Col>
                <Col lg={4} className='mb-3 d-flex align-items-end'>
                  <Button color='primary' size='sm' className='mb-0' disabled={isBusy} onClick={onRunNightly}>
                    {isBusy ? <><Spinner size='sm' className='mr-2' />Working...</> : "Run tonight's job now"}
                  </Button>
                </Col>
              </Row>

              {message && <Alert color='success' className='alert--bordered' icon><p>{message}</p></Alert>}
              {error && <Alert color='danger' className='alert--bordered' icon><p>{error}</p></Alert>}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Card>
            <CardHeader><strong>CORT: add an agent through Salesforce</strong></CardHeader>
            <CardBody>
              <div className='card__title'>
                <h5 className='subhead'>Sends CORT&apos;s Salesforce &quot;add participant&quot; to the CORT program, through the same code.</h5>
              </div>
              <Row>
                <Col lg={4} className='mb-3'><Field id='cort-sf-email' label='New agent email' type='email' value={newAgent.email} onChange={updateNewAgent('email')} /></Col>
                <Col lg={4} className='mb-3'><Field id='cort-sf-first' label='First name' value={newAgent.first_name} onChange={updateNewAgent('first_name')} /></Col>
                <Col lg={4} className='mb-3'><Field id='cort-sf-last' label='Last name' value={newAgent.last_name} onChange={updateNewAgent('last_name')} /></Col>
              </Row>
              <Button color='primary' size='sm' className='mb-3' disabled={!newAgent.email || isBusy} onClick={onAddThroughSalesforce}>Add through Salesforce</Button>

              {addedAgent && <PersonCard person={addedAgent} />}
              {addError && <Alert color='danger' className='alert--bordered' icon><p>{addError}</p></Alert>}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default CortActivationTool;
